import * as yup from 'yup';

import {
  EWebinarWithSetFragment,
  FullSetFragment,
  RegistrationFieldType,
  RegistrationFormField,
  RegistrationFormFieldInput,
  RegistrationFormSettingsInput,
  UpdateEwebinarInput,
} from '$/GraphQL/schema/schema';
import { normalizePropertyName } from '$/utils/string';
import stripTypenames from '$/utils/stripTypenames';
import { SettingsComponent } from '../../SettingsComponent';

import { defaultFullNameField } from '$/utils/attendeeFormField';
import { RegFormSettingsForForm } from './types';

/**
 * Handle prepareDefaultValues - prepareSubmit in registration form settings
 */
export class RegistrationForm extends SettingsComponent<RegFormSettingsForForm> {
  public prepareDefaultValues(ewebinar: EWebinarWithSetFragment): RegFormSettingsForForm {
    const strippedTypenamesFields = stripTypenames(ewebinar.registrationFormSettings.fields);
    ['firstName', 'lastName', 'email'].forEach((propertyName, index) => {
      if (!strippedTypenamesFields[index].propertyName)
        strippedTypenamesFields[index].propertyName = propertyName;
    });
    const hiddenFields = strippedTypenamesFields.filter(
      (field, index) => field.type === RegistrationFieldType.Hidden && index !== 1
    );
    const otherFields = strippedTypenamesFields.filter(
      ({ type }, index) => type !== RegistrationFieldType.Hidden || index === 1
    );

    return {
      ...(ewebinar.registrationFormSettings as RegFormSettingsForForm),
      useFullNameField: ewebinar.registrationFormSettings.useFullNameField ? true : false,

      fields: [...otherFields, ...hiddenFields],
      fullNameField: ewebinar.registrationFormSettings.fullNameField ?? defaultFullNameField,
    };
  }

  public prepareSubmit(
    formValues: RegFormSettingsForForm,
    set: FullSetFragment
  ): UpdateEwebinarInput {
    const ewebinar = set.draftWebinar;
    // Copy object
    formValues = { ...formValues };

    // propertyName must be either a valid value or NULL (not allow an empty string)
    const normalizeField = (
      field: RegistrationFormField,
      index: number
    ): RegistrationFormFieldInput => {
      let propertyNameInput = field.propertyName;
      const builtInPropertyNames = ['firstName', 'lastName', 'email'];
      if (!propertyNameInput && builtInPropertyNames[index]) {
        propertyNameInput = builtInPropertyNames[index];
      }
      const propertyName = normalizePropertyName(propertyNameInput);
      let options;
      if (field.type === RegistrationFieldType.Selection) {
        options =
          field.options && field.options.length > 0
            ? field.options.filter((option) => option.text)
            : [];
      }

      return {
        ...field,
        fieldName:
          field.type === RegistrationFieldType.Hidden && propertyName
            ? propertyName!
            : field.fieldName,
        isRequired: field.type === RegistrationFieldType.Hidden ? false : field.isRequired,
        propertyName,
        options,
      };
    };

    const formSettingsInput: RegistrationFormSettingsInput = {
      title: formValues.title ?? '',
      fields: formValues.fields?.map(normalizeField) ?? [],
      fullNameField: formValues.fullNameField,
      useFullNameField: formValues.useFullNameField,
      showConsentCheckbox: Boolean(formValues.showConsentCheckbox),
      registerButton: formValues.registerButton || '',
      consentCheckboxText: formValues.consentCheckboxText || '',
      collectedFormFieldsConsent: formValues.collectedFormFieldsConsent,
    };

    return {
      id: ewebinar.id,
      registrationFormSettings: formSettingsInput,
    };
  }

  public getValidationSchema(): yup.AnyObjectSchema {
    // TODO: Bring this up
    return yup.object();
  }
}

export default RegistrationForm;
